import { Alert, Spin } from 'antd'
import React, { useEffect, useRef } from 'react'
import styled, { css, keyframes } from 'styled-components/macro'
import { HARBOUR_DEFAULT_PLAN_FILTER } from 'src/constants/harbour'
import {
  HARBOUR_PULSE_TONE,
  getHarbourPulseTiles,
  nextHarbourWonCelebrateState,
} from 'src/utils/harbourPulse'

/**
 * First 0 → N on this plan filter celebrates; a count that was already
 * above 0 stays quiet. Previous is keyed by plan so Paid/All/Free churn
 * does not look like a new win.
 */
function useHarbourWonThisWeekCelebrate({
  recoveredThisWeek,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  loading = false,
  error = null,
} = {}) {
  const previousByPlanRef = useRef({})
  const celebrateByPlanRef = useRef({})
  const plan = String(planFilter || HARBOUR_DEFAULT_PLAN_FILTER)
  const recovered = Number(recoveredThisWeek) || 0
  const previous =
    plan in previousByPlanRef.current ? previousByPlanRef.current[plan] : null

  const celebrateWon = nextHarbourWonCelebrateState({
    recoveredThisWeek: recovered,
    previousRecoveredThisWeek: previous,
    celebrateWon: Boolean(celebrateByPlanRef.current[plan]),
  })
  celebrateByPlanRef.current[plan] = celebrateWon

  useEffect(() => {
    if (loading || error) {
      return undefined
    }
    previousByPlanRef.current[plan] = recovered
    return undefined
  }, [error, loading, plan, recovered])

  return celebrateWon
}

/**
 * Harbour health radar — counts only. Iceberg vs Needs you cap is the product.
 */
const HarbourPulse = ({
  pulse,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  loading = false,
  error = null,
  onRetry,
}) => {
  const celebrateWon = useHarbourWonThisWeekCelebrate({
    recoveredThisWeek: pulse?.recoveredThisWeek,
    planFilter,
    loading,
    error,
  })
  const tiles = getHarbourPulseTiles(pulse, { celebrateWon })

  return (
    <Strip data-testid='harbour-pulse' data-compact='true'>
      {error ? (
        <ErrorAlert
          type='error'
          showIcon
          message={error}
          action={
            onRetry ? (
              <button
                type='button'
                onClick={onRetry}
                data-testid='harbour-pulse-retry'
              >
                Retry
              </button>
            ) : null
          }
        />
      ) : null}
      {loading && !error ? (
        <Loading data-testid='harbour-pulse-loading'>
          <Spin size='small' />
        </Loading>
      ) : null}
      <Tiles data-testid='harbour-pulse-tiles'>
        {tiles.map(tile => (
          <Tile
            key={tile.key}
            $tone={tile.tone}
            title={tile.tip}
            data-testid={`harbour-pulse-${tile.key}`}
            data-tone={tile.tone}
            data-shout={tile.shout ? 'true' : 'false'}
          >
            <Value>{tile.value}</Value>
            <Label>{tile.label}</Label>
            {tile.icebergCaption ? (
              <Caption data-testid='harbour-pulse-iceberg'>
                {tile.icebergCaption}
              </Caption>
            ) : null}
          </Tile>
        ))}
      </Tiles>
    </Strip>
  )
}

const wonPulse = keyframes`
  0% {
    background-color: rgba(124, 179, 66, 0.28);
  }
  100% {
    background-color: rgba(124, 179, 66, 0.12);
  }
`

const Strip = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};
`

const Loading = styled.div`
  min-height: 24px;
`

const ErrorAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing.sm || '8px'};
`

const Tiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};
`

const Tile = styled.div`
  min-width: 5.75rem;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === HARBOUR_PULSE_TONE.SHOUT
        ? '#E8C4C3'
        : $tone === HARBOUR_PULSE_TONE.WON
        ? '#B7D4C0'
        : $tone === HARBOUR_PULSE_TONE.ICEBERG
        ? '#D7E3DC'
        : '#E6E8EB'};
  background: ${({ $tone }) =>
    $tone === HARBOUR_PULSE_TONE.SHOUT
      ? '#FDF6F6'
      : $tone === HARBOUR_PULSE_TONE.WON
      ? '#EAF6EE'
      : $tone === HARBOUR_PULSE_TONE.ICEBERG
      ? '#F4F8F6'
      : '#FAFBFC'};
  color: ${({ $tone, theme }) =>
    $tone === HARBOUR_PULSE_TONE.SHOUT
      ? '#A94442'
      : $tone === HARBOUR_PULSE_TONE.MUTED
      ? theme.colours.oliveGrey || '#888'
      : theme.colours.broccoliGreen || '#1a1a1a'};
  ${({ $tone }) =>
    $tone === HARBOUR_PULSE_TONE.WON &&
    css`
      animation: ${wonPulse} 1.2s ease-in-out 3;
    `}
`

const Value = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
`

const Label = styled.div`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
`

const Caption = styled.div`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

export default HarbourPulse
